import React, { useEffect, useState } from 'react';
import { InputField } from 'lincd-input/lib/components/InputField';
import { useTranslate } from '@tolgee/react';
import { Button } from 'lincd-mui-base/lib/components/Button';
import style from './JoinInputButton.scss.json';
import './JoinInputButton.scss';
import { Spinner } from '../atoms/Spinner';
import { Team } from 'lincd-irlcg/lib/shapes/Team';
import { useAuth } from 'lincd-auth/lib/hooks/useAuth';
import { Player } from 'lincd-irlcg/lib/shapes/Player';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { Dialog } from '@capacitor/dialog';
import { cl } from 'lincd/lib/utils/ClassNames';
import { withRetry } from 'lincd-irlcg/lib/utils/helper';

interface JoinInputButtonProps {
  description?: string;
}
const JoinInputButton = ({ description }: JoinInputButtonProps) => {
  let { t } = useTranslate();
  let prefix = 'joinNumberTeam';
  const [poetNumber, setPoetNumber] = useState<string>('');
  let [loading, setLoading] = useState(false);
  const user = useAuth().user as Player;
  const navigate = useNavigate();

  const handlePoetNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.replace(/[^0-9]/g, '');
    setPoetNumber(inputValue);
  };

  const joinTeam = async () => {
    // check poet number not empty
    if (!poetNumber) {
      alert(t(prefix + '.enterTeamNumber', 'Please enter Peace Team Number'));
      return;
    }

    setLoading(true);

    // check if user is in trial/event mode, they will be removed from the team once they join a number team
    // if user is NOT in either trial/event mode, they can join another team while remaining in previous team
    let isUserInCertainMode =
      user?.isInCertainMode('event') || user?.isInCertainMode('trial');

    try {
      // check if team exists or not if exists add user to team
      const result = await withRetry(() =>
        Team.addUserToTeam(
          parseInt(poetNumber),
          user,
          isUserInCertainMode,
          true,
        ),
      );

      //Now that the user has setup the profile AND joined a team then can start the game
      if (result.team) {
        try {
          // add user to all action
          await withRetry(() => Team.addUserToAction(user));

          // set user.profileSetupCompleted to true after addUserToTeam is success
          // to prevent the user from navigating back or manually entering the URL to the home page
          // and that will make a new bug "user does not have a team"
          if (user) {
            user.profileSetupCompleted = true;
          }

          // set current team on frontend
          if (user?.currentTeam) {
            user.currentTeam = result.team;
          }

          navigate(ROUTES.index.path);
        } catch (actionError) {
          console.error('Error adding user to action:', actionError);
          await Dialog.alert({
            title: t(prefix + '.actionErrorTitle', 'Action Error'),
            message: t(
              prefix + '.actionErrorMessage',
              'Hmm, things are a little crowded right now. We were not able to add you to the team. Please try again in a moment.',
            ),
          });
        }
      } else if (result.error) {
        await Dialog.alert({
          title: 'Something went wrong',
          message: result.error.message,
        });
      } else {
        await Dialog.alert({
          title: t(prefix + '.teamNotFoundTitle', 'Team not found'),
          message: t(
            prefix + '.teamNotFoundMessage',
            `We couldn’t locate the ${poetNumber} team. Could you double-check the name and try again?`,
          ),
        });
      }
    } catch (err) {
      console.error(`Error joining ${poetNumber}:`, err);
      await Dialog.alert({
        title: t(prefix + '.joinErrorTitle', 'Join Error'),
        message: t(
          prefix + '.joinErrorMessage',
          `Hmm, things are a little crowded right now. We were not able to add you to the team. Please try again in a moment.`,
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <InputField
        placeholder={t(prefix + '.teamNumberPlaceholder', 'Peace Team Number')}
        value={poetNumber}
        type="text"
        onChange={handlePoetNumber}
        pattern={'[0-9]*'}
        required={true}
        autoFocus
        aria-label={t(prefix + '.teamNumberInput', 'Peace Team Number input')}
      />
      <Button
        variant={'filled'}
        className={cl(style.buttonJoinTeam, loading && style.loading)}
        color={'primary'}
        onClick={joinTeam}
        fullWidth={true}
        disabled={loading}
        aria-label={t(prefix + '.joinTeamButton', 'Join team button')}
      >
        {loading ? (
          <Spinner color="white" />
        ) : (
          <>{t(prefix + '.joinPeaceTeam', 'Join Peace Team')}</>
        )}
      </Button>

      <p aria-labelledby={t(prefix + '.noNumberLabel', 'No Team Number Info')}>
        {t(prefix + '.noNumber', description)}
      </p>
    </div>
  );
};

export default JoinInputButton;
